import React, { cloneElement, isValidElement } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@shadcn";
import { Check, Clock, Sparkles } from "lucide-react";

export type PerkCardTemplateProps = {
  title: string;
  text: string;
  isSelected: boolean;
  isAvailable: boolean;
  icon: JSX.Element;
  onClick: any;
  includedInPackage: boolean;
  isBeta?: boolean;
};

export const PerkCardTemplate = ({
  title,
  text,
  isSelected,
  isAvailable,
  icon,
  onClick,
  includedInPackage,
  isBeta,
}: PerkCardTemplateProps): JSX.Element => {
  const { t } = useTranslation();
  const renderedIcon =
    isValidElement(icon) && (icon as any).props
      ? (() => {
          const baseSize =
            (icon as any).props?.size ??
            (icon as any).props?.width ??
            (icon as any).props?.height ??
            24;
          const nextSize = Math.round(baseSize * 1.25);
          return cloneElement(icon as any, {
            size: nextSize,
            width: nextSize,
            height: nextSize,
          });
        })()
      : icon;

  const statusConfig = isAvailable
    ? {
        label: t("perks.status.active"),
        badgeClass: "bg-white border-green-200 text-green-700",
        iconWrapperClass: "bg-green-500 text-white h-3 w-3",
        iconSizeClass: "h-1.5 w-1.5",
        Icon: Check,
      }
    : isBeta
      ? {
          label: t("perks.status.beta"),
          badgeClass: "bg-white border-primary-mediumish text-primary-medium",
          iconWrapperClass: "bg-primary-mediumish text-white h-3 w-3",
          iconSizeClass: "h-1.5 w-1.5",
          Icon: Sparkles,
        }
      : {
          label: t("perks.status.comingSoon"),
          badgeClass: "bg-white border-neutral-light text-neutral-dark",
          iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
          iconSizeClass: "h-2.5 w-2.5",
          Icon: Clock,
        };

  return (
    <button
      onClick={onClick}
      style={{ height: 234 }}
      className={`group relative flex flex-col w-52 pt-3 pl-5 pb-3 pr-3 bg-white border border-neutral-light rounded-2xl 
      hover:border-primary-mediumish transition ease-in-out delay-0 duration-200 transition transform scale-100 hover:scale-105
      `}
    >
      <div className="flex flex-col w-full h-auto items-start justify-start">
        <div className="flex w-full justify-end">
          <Badge
            variant="outline"
            className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${statusConfig.badgeClass}`}
          >
            <span className={`flex items-center justify-center rounded-full ${statusConfig.iconWrapperClass}`}>
              <statusConfig.Icon className={statusConfig.iconSizeClass} />
            </span>
            <span>{statusConfig.label}</span>
          </Badge>
        </div>
        <div className="flex flex-row border-0 p-2 mt-2 rounded-full bg-neutral-light self-start -translate-y-5 duration-200 ease-in-out">
          {renderedIcon}
        </div>
        <div className="h-full w-full mt-2">
          <div className="text-left w-full">
            <div className="my-1 w-40 font-semibold text-lg leading-6 text-neutral-darkest">
              {title}
            </div>
            <div className="font-normal text-sm leading-5 text-neutral-darkest">
              {text}
            </div>
          </div>
        </div>
      </div>
    </button>
  );
};
